import axios, { CancelTokenSource } from 'axios';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { PxDropdownSc } from 'components/final-form/Select';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import LoaderComponent from 'components/LoaderComponent';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { DropdownProps, Grid, Header, Icon, Table } from 'semantic-ui-react';
import { getAllCodeGroups } from 'services/codeGroupServices';
import { getAllCodesByCodeGroupId } from 'services/codeServices';
import styled from 'styled-components';
import { BaseEntityCodeDto, CodeDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';

const ScrollableTableWrapper = styled.div<IndexSignature>`
  height: ${p => p.height ? p.height : "350px"};
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const StyledBtnContainer = styled.div`
  margin-bottom: 5px;
  white-space: nowrap;
`;

const StyledButtons = styled(Grid.Column)`
  padding: 0 !important;
  margin: 0 4px 0 4px !important;
  flex: 1 1 auto;
`;

const StyledTableLeft = styled(Grid.Column)`
    padding-left: 1rem !important;
    padding-right: 0 !important;
    flex: 50 50 auto;
`;

const StyledTableRight = styled(Grid.Column)`
    padding-right: 1rem !important;
    padding-left: 0 !important;
    flex: 50 50 auto;
`;

const NoDataDiv = styled.div`
  font-size: 1rem;
  color: #444;
  font-style: italic;
  padding: 0.7rem 1rem;
`;

interface Props extends MessageSourceProps {
  selectedCodes: Array<BaseEntityCodeDto>,
  moveToSelected: (code: CodeDto) => void,
  moveAllToSelected: (codes: Array<CodeDto>) => void
  removeFromSelected: (code: BaseEntityCodeDto) => void
  removeAllFromSelected: () => void,
  activeIndex?: number,
  readOnly: boolean
}

interface State {
  codeGroups: Array<DropdownOption>,
  codeGroupLoaded: boolean,
  selectedCodeGroup: any,
  availableCodes: Array<CodeDto>,
  filteredAvailableCodes: Array<CodeDto>,
  markAllAvailable: boolean,
  markAllSelected: boolean,
  markedAvailableCode: number,
  markedSelectedCode: number,
  cancelTokenSource: CancelTokenSource,
  activeIndex?: number
}

class CodeGroupsSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const {activeIndex} = this.props;

    this.state = {
      codeGroups: [],
      codeGroupLoaded: false,
      selectedCodeGroup: -1,
      availableCodes: [],
      filteredAvailableCodes: [],
      markAllAvailable: false,
      markAllSelected: false,
      markedAvailableCode: -1,
      markedSelectedCode: -1,
      cancelTokenSource,
      activeIndex: activeIndex
    };

    getAllCodeGroups(false, cancelTokenSource).then(response => {
      this.setState({
        codeGroups: mapToDropdownOptionArray(response, "description"),
        selectedCodeGroup: response.length > 0 ? response[0].id : -1
      });

      if (response.length > 0) {
        this.getCodesByCodeGroupId(response[0].id);
      }
    }).catch(noop);
  }

  componentDidUpdate({selectedCodes: prevSelectedCodes}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {selectedCodes} = this.props;

    if (selectedCodes.length != prevSelectedCodes.length) {
      this.filterAvailableCodes();
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  filterAvailableCodes = () => {

    const {selectedCodes} = this.props;
    const {availableCodes} = this.state;

    const selectedCodeIds = selectedCodes.map(selectedCode => selectedCode.codeId);
    const selectedCodeSet: Set<number> = new Set(selectedCodeIds);

    const filteredAvailableCodes = availableCodes
        .filter(availableCode => !selectedCodeSet.has(availableCode.id));

    this.setState({
      filteredAvailableCodes: filteredAvailableCodes,
      codeGroupLoaded: true
    })
  };

  getCodesByCodeGroupId(selectedCodeGroup: number) {
    const {cancelTokenSource} = this.state;

    getAllCodesByCodeGroupId(selectedCodeGroup, cancelTokenSource).then(response => {
          this.setState({
            availableCodes: response
          });

          this.filterAvailableCodes();
        })
        .catch(noop)
        .finally(() => this.setState({codeGroupLoaded: true}))
  }

  onCodeGroupChange = (value: any) => {
    this.setState({
      selectedCodeGroup: value,
      codeGroupLoaded: false
    });

    this.getCodesByCodeGroupId(value);
  };

  markAvailableCode = (id: number) => {
    this.setState({
      markedAvailableCode: id
    })
  };

  markSelectedCode = (id: number) => {
    this.setState({
      markedSelectedCode: id
    })
  };

  markAllAvailable = () => {
    this.setState(({filteredAvailableCodes}: State) => ({
      markAllAvailable: filteredAvailableCodes.length > 0
    }))
  };

  markAllSelected = () => {
    const {selectedCodes} = this.props;

    this.setState({
      markAllSelected: selectedCodes.length > 0
    })
  };

  clearAvailable = () => {
    this.setState({
      markAllAvailable: false,
      markedAvailableCode: -1
    })
  };

  clearSelected = () => {
    this.setState({
      markAllSelected: false,
      markedSelectedCode: -1
    })
  };

  resetAfterAction = () => {
    this.clearSelected();
    this.clearAvailable();
  };

  moveToSelected = () => {
    const {markedAvailableCode, markAllAvailable, availableCodes} = this.state;
    const {moveToSelected, moveAllToSelected} = this.props;

    if (markAllAvailable) {
      moveAllToSelected(availableCodes);
    } else {
      const availableCode = availableCodes.find(availableCode => availableCode.id == markedAvailableCode);
      if (availableCode) {
        moveToSelected(availableCode);
      }
    }

    this.resetAfterAction();
  };

  moveToAvailable = () => {
    const {markedSelectedCode, markAllSelected} = this.state;
    const {removeFromSelected, removeAllFromSelected, selectedCodes} = this.props;

    if (markAllSelected) {
      removeAllFromSelected();
    } else {
      const selectedCode = selectedCodes.find(selectedCode => selectedCode.codeId == markedSelectedCode);
      if (selectedCode) {
        removeFromSelected(selectedCode);
      }
    }

    this.resetAfterAction();
  };

  render(): React.ReactNode {

    const {selectedCodes, getMessage, readOnly} = this.props;
    const {
      codeGroups,
      filteredAvailableCodes,
      selectedCodeGroup,
      markAllAvailable,
      markAllSelected,
      codeGroupLoaded
    } = this.state;
    const {markedAvailableCode, markedSelectedCode} = this.state;

    const showContent = filteredAvailableCodes.length > 0 || selectedCodes.length > 0;
    const showCodeGroupOptions = codeGroups.length > 0;

    const mappedAvailableCodes = filteredAvailableCodes.map(availableCode => {
      return (
          <Table.Row key={availableCode.id}
                     onClick={() => this.markAvailableCode(availableCode.id)}
                     onDoubleClick={this.moveToSelected}
                     active={markAllAvailable || markedAvailableCode == availableCode.id}
          >
            <Table.Cell>{availableCode.codeId}</Table.Cell>
            <Table.Cell>{availableCode.description}</Table.Cell>
          </Table.Row>
      );
    });

    const mappedSelectedCodes = selectedCodes ? selectedCodes.map(selectedCode => {
      return (
          <Table.Row key={selectedCode.codeId}
                     onClick={() => this.markSelectedCode(selectedCode.codeId)}
                     onDoubleClick={this.moveToAvailable}
                     active={markAllSelected || markedSelectedCode == selectedCode.codeId}
          >
            <Table.Cell>{selectedCode.code}</Table.Cell>
            <Table.Cell>{selectedCode.description}</Table.Cell>
          </Table.Row>
      );
    }) : [];

    return showCodeGroupOptions ?
        <PraxFormGrid style={{paddingBottom: 30}}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">{getMessage("patient.codeGroups")}:</Grid.Column>
            <Grid.Column width={7}>
              <PxDropdownSc
                  value={selectedCodeGroup}
                  onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) =>
                      this.onCodeGroupChange(data.value)}
                  options={codeGroups}
                  disabled={readOnly}
                  selection
                  search
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <StyledTableLeft>
              {!codeGroupLoaded
                  ? <LoaderComponent message={getMessage("codeGroup.loading")}/>
                  : showContent
                      ? this.renderTableSegment(getMessage("available"), mappedAvailableCodes)
                      : <NoDataDiv>{getMessage("codeGroup.noCodesFoundForCodeGroup")}</NoDataDiv>
              }
            </StyledTableLeft>
            {(showContent) &&
            <StyledButtons textAlign="center" verticalAlign="middle">
              <StyledBtnContainer>
                <StyledIcon bordered
                            onClick={this.clearAvailable}
                            disabled={readOnly}
                            name="clipboard outline"
                />
                <StyledIcon bordered
                            onClick={this.clearSelected}
                            disabled={readOnly}
                            name="clipboard outline"
                />
              </StyledBtnContainer>
              <StyledBtnContainer>
                <StyledIcon bordered
                            onClick={this.markAllAvailable}
                            disabled={readOnly}
                            name="clipboard list"
                />
                <StyledIcon bordered
                            onClick={this.markAllSelected}
                            disabled={readOnly}
                            name="clipboard list"
                />
              </StyledBtnContainer>
              <StyledBtnContainer>
                <StyledIcon bordered
                            onClick={this.moveToSelected}
                            disabled={(!markAllAvailable && markedAvailableCode == -1) || readOnly}
                            name="angle double right"
                />
                <StyledIcon bordered
                            onClick={this.moveToAvailable}
                            disabled={(!markAllAvailable && markedSelectedCode == -1) || readOnly}
                            name="angle double left"
                />
              </StyledBtnContainer>
            </StyledButtons>
            }
            <StyledTableRight>
              {!codeGroupLoaded
                  ? <LoaderComponent message={getMessage("codeGroup.loading")}/>
                  : showContent
                      ? this.renderTableSegment(getMessage("selected"), mappedSelectedCodes)
                      : ""
              }
            </StyledTableRight>
          </Grid.Row>
        </PraxFormGrid>
        : <NoDataDiv>{getMessage("codeGroup.noCodeGroupsFound")}</NoDataDiv>
        ;
  }

  renderTableSegment = (headerText: string, mappedCodes: Array<React.ReactNode>): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Header as='h5'>
            {headerText}:
          </Header>
          <ScrollableTableWrapper>
            <Table celled selectable size='small'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{getMessage("id")}</Table.HeaderCell>
                  <Table.HeaderCell>{getMessage("description")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {mappedCodes}
              </Table.Body>
            </Table>
          </ScrollableTableWrapper>
        </React.Fragment>
    )
  }
}

export default withMessages(CodeGroupsSegment);
